import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface MonthRangePickerProps {
  min_month: string | null;
  max_month: string | null;
  date_from: string | null;
  date_to: string | null;
  onChange: (date_from: string | null, date_to: string | null) => void;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseMonth(iso: string | null): { year: number; month: number } | null {
  if (!iso) return null;
  const [y, m] = iso.split("T")[0].split("-").map(Number);
  return { year: y, month: m - 1 };
}

function toIso(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-01T00:00:00`;
}

function formatLabel(iso: string | null): string {
  const m = parseMonth(iso);
  return m ? `${MONTH_NAMES[m.month]} ${m.year}` : "";
}

function monthIndex(year: number, month: number): number {
  return year * 12 + month;
}

export interface SingleMonthPickerProps {
  label: string;
  value: string | null;
  min_month: string | null;
  max_month: string | null;
  notBefore?: { year: number; month: number } | null;
  onSelect: (iso: string) => void;
  onClear: () => void;
}

export function SingleMonthPicker({
  label,
  value,
  min_month,
  max_month,
  notBefore,
  onSelect,
  onClear,
}: SingleMonthPickerProps) {
  const min = parseMonth(min_month);
  const max = parseMonth(max_month);
  const selected = parseMonth(value);

  const initialYear =
    selected?.year ?? (notBefore ? notBefore.year : null) ?? max?.year ?? new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState<number>(initialYear);

  useEffect(() => {
    if (!open) return;
    setViewYear(
      selected?.year ?? (notBefore ? notBefore.year : null) ?? max?.year ?? new Date().getFullYear()
    );
  }, [open]);

  const minIdx = min ? monthIndex(min.year, min.month) : -Infinity;
  const maxIdx = max ? monthIndex(max.year, max.month) : Infinity;
  const notBeforeIdx = notBefore ? monthIndex(notBefore.year, notBefore.month) : -Infinity;

  const isDisabled = (year: number, month: number): boolean => {
    const i = monthIndex(year, month);
    return i < minIdx || i > maxIdx || i < notBeforeIdx;
  };

  const isSelected = (year: number, month: number): boolean => {
    if (!selected) return false;
    return selected.year === year && selected.month === month;
  };

  const canGoPrevYear = !min || viewYear - 1 >= min.year;
  const canGoNextYear = !max || viewYear + 1 <= max.year;

  const valueLabel = formatLabel(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`flex items-center gap-1.5 h-8 px-3 rounded-lg font-medium transition-all duration-150 ${
            valueLabel
              ? "bg-primary/10 border-primary/50 text-primary hover:bg-primary/15"
              : "hover:bg-accent"
          }`}
        >
          <CalendarIcon className="h-3 w-3 opacity-60 shrink-0" />
          <span>{label}</span>
          {valueLabel && (
            <Badge variant="secondary" className="px-1.5 py-0 text-xs font-normal bg-primary/20 text-primary border-0">
              {valueLabel}
            </Badge>
          )}
          <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        {/* Year navigation */}
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={!canGoPrevYear}
            onClick={() => setViewYear(y => y - 1)}
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold">{viewYear}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={!canGoNextYear}
            onClick={() => setViewYear(y => y + 1)}
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-4 gap-1.5">
          {MONTH_NAMES.map((name, idx) => {
            const disabled = isDisabled(viewYear, idx);
            const sel = isSelected(viewYear, idx);
            return (
              <button
                key={name}
                type="button"
                disabled={disabled}
                onClick={() => {
                  onSelect(toIso(viewYear, idx));
                  setOpen(false);
                }}
                className={cn(
                  "h-8 px-2 text-xs rounded-md transition-colors",
                  disabled && "text-muted-foreground/40 cursor-not-allowed",
                  !disabled && !sel && "hover:bg-accent hover:text-accent-foreground",
                  sel && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                )}
              >
                {name}
              </button>
            );
          })}
        </div>

        {/* Clear */}
        {value && (
          <div className="mt-3 pt-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full gap-1.5"
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              <X className="h-3 w-3" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const MonthRangePicker = ({ min_month, max_month, date_from, date_to, onChange }: MonthRangePickerProps) => {
  const fromParsed = parseMonth(date_from);

  const handleFromSelect = (iso: string) => {
    const newFrom = parseMonth(iso);
    const currentTo = parseMonth(date_to);
    if (newFrom && currentTo && monthIndex(newFrom.year, newFrom.month) > monthIndex(currentTo.year, currentTo.month)) {
      onChange(iso, null);
    } else {
      onChange(iso, date_to);
    }
  };

  return (
    <>
      <SingleMonthPicker
        label="Start"
        value={date_from}
        min_month={min_month}
        max_month={max_month}
        onSelect={handleFromSelect}
        onClear={() => onChange(null, null)}
      />
      <SingleMonthPicker
        label="End"
        value={date_to}
        min_month={min_month}
        max_month={max_month}
        notBefore={fromParsed}
        onSelect={(iso) => onChange(date_from, iso)}
        onClear={() => onChange(date_from, null)}
      />
    </>
  );
};

export default MonthRangePicker;
